
import { Link } from "../router";
import { usePathname } from "../router";
import { useEffect, useState, type MouseEvent, type ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { useFocusTrap } from "../../hooks/use-focus-trap";
import { MEDIA, useMediaQuery } from "../breakpoints";
import { holdUntilExitEnds } from "../motion";
import { BottomBar } from "./BottomBar";
import { HOME_HREF, activeNavItem } from "./nav";
import { setSidebarPreference, useSidebarPreference } from "./preferences";
import { useShellState } from "./shell-context";
import styles from "./shell.module.css";
import { Sidebar } from "./Sidebar";
import { useDocumentTitle } from "./use-document-title";
import { useKeyboardOpen } from "./use-keyboard-open";
import { UserMenu, type ShellUser } from "./UserMenu";

export const MAIN_CONTENT_ID = "admin-main";

type Props = {
  user: ShellUser | null;
  loading: boolean;
  onSignOut: () => void;
  children: ReactNode;
};

function useExitHold(open: boolean): boolean {
  const [held, setHeld] = useState({ open, closing: false });

  if (held.open !== open) setHeld({ open, closing: !open });

  useEffect(() => {
    if (!held.closing) return;
    return holdUntilExitEnds(() => setHeld((current) => ({ ...current, closing: false })));
  }, [held.closing]);

  return held.closing;
}

function ShellSkeleton({ label }: { label: string }) {
  return (
    <div className={styles.skeleton} aria-busy="true" role="status">
      <span className={styles.srOnly}>{label}</span>
      <div className={`${styles.skeletonBlock} ${styles.skeletonTitle}`} aria-hidden="true" />
      <div className={`${styles.skeletonBlock} ${styles.skeletonMeta}`} aria-hidden="true" />
      {[0, 1, 2, 3, 4, 5].map((row) => (
        <div key={row} className={`${styles.skeletonBlock} ${styles.skeletonRow}`} aria-hidden="true" />
      ))}
    </div>
  );
}

export function AppShell({ user, loading, onSignOut, children }: Props) {
  const pathname = usePathname();
  const { t } = useTranslation();
  const { pageTitle, immersive } = useShellState();
  const sidebarPreference = useSidebarPreference();
  const isPhone = useMediaQuery(MEDIA.phone);
  const isTablet = useMediaQuery(MEDIA.tablet);
  const isLaptopUp = useMediaQuery(MEDIA.laptopUp);
  const isDesktop = useMediaQuery(MEDIA.desktopUp);
  const keyboardOpen = useKeyboardOpen();
  const [overlayPath, setOverlayPath] = useState<string | null>(null);
  const [morePath, setMorePath] = useState<string | null>(null);

  const overlayOpen = isTablet && overlayPath === pathname;
  const overlayClosing = useExitHold(overlayOpen) && isTablet;
  const overlayState = overlayOpen ? "open" : overlayClosing ? "closing" : undefined;
  const moreOpen = isPhone && morePath === pathname;
  const expanded = isDesktop ? sidebarPreference !== "rail" : isLaptopUp ? sidebarPreference === "expanded" : overlayOpen;
  const rail = (isTablet || isLaptopUp) && !expanded;
  const trapRef = useFocusTrap(overlayOpen);

  const brand = t("common.brandName");
  const navItem = activeNavItem(pathname);
  const title = pageTitle ?? (navItem ? t(`nav.${navItem.id}`) : brand);

  useDocumentTitle(title);

  useEffect(() => {
    if (!overlayOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !event.defaultPrevented) setOverlayPath(null);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [overlayOpen]);

  const toggleLabel = isTablet
    ? t(overlayOpen ? "nav.shell.closeSidebar" : "nav.shell.openSidebar")
    : t(expanded ? "nav.shell.collapseSidebar" : "nav.shell.expandSidebar");

  const onToggle = () => {
    if (isTablet) {
      setOverlayPath(overlayOpen ? null : pathname);
      return;
    }
    if (isDesktop) setSidebarPreference(expanded ? "rail" : null);
    else setSidebarPreference(expanded ? null : "expanded");
  };

  const onSkip = (event: MouseEvent<HTMLAnchorElement>) => {
    const main = document.getElementById(MAIN_CONTENT_ID);
    if (!main) return;
    event.preventDefault();
    main.focus();
    main.scrollIntoView({ block: "start" });
  };

  return (
    <div
      className={styles.shell}
      data-immersive={immersive || undefined}
      data-keyboard={keyboardOpen || undefined}
      data-overlay={overlayState}
    >
      <a href={`#${MAIN_CONTENT_ID}`} className={styles.skipLink} onClick={onSkip}>
        {t("nav.shell.skipToContent")}
      </a>
      <Sidebar
        pathname={pathname}
        expanded={expanded}
        rail={rail}
        overlay={overlayOpen}
        closing={overlayClosing}
        toggleLabel={toggleLabel}
        onToggle={onToggle}
        onNavigate={() => setOverlayPath(null)}
        user={user}
        onSignOut={onSignOut}
        trapRef={trapRef}
      />
      {overlayState && (
        <button
          type="button"
          tabIndex={-1}
          aria-hidden="true"
          className={`${styles.blanket} ${styles.sidebarBlanket}`}
          onClick={() => setOverlayPath(null)}
        />
      )}
      <div className={styles.frame}>
        <header className={styles.topBar}>
          <Link href={HOME_HREF} className={styles.topBrand} aria-label={t("nav.shell.home")}>
            <img src="/mark.svg" alt="" className={styles.brandMark} />
          </Link>
          <p className={styles.topTitle} aria-hidden="true">{title}</p>
          <UserMenu user={user} onSignOut={onSignOut} compact side="bottom" />
        </header>
        <main id={MAIN_CONTENT_ID} tabIndex={-1} className={styles.main}>
          <div className={styles.content}>
            {loading ? <ShellSkeleton label={t("nav.shell.loading")} /> : children}
          </div>
        </main>
        <div className={styles.bottomSpacer} aria-hidden="true" />
      </div>
      <BottomBar
        pathname={pathname}
        role={user?.role ?? null}
        moreOpen={moreOpen}
        onMoreOpenChange={(open) => setMorePath(open ? pathname : null)}
        onSignOut={onSignOut}
      />
    </div>
  );
}
